import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TablePagination from "@/components/masters/TablePagination";
import { Search, FileText } from "lucide-react";

// Mock collector performance data
const INITIAL_COLLECTOR_DATA = [
  {
    id: 1,
    collector: "John Smith",
    zone: "North",
    totalCollected: 185600,
    target: 200000,
    transactions: 245,
    efficiency: 92.8,
    consumerRating: 4.8,
  },
  {
    id: 2,
    collector: "Emma Wilson",
    zone: "South",
    totalCollected: 175200,
    target: 180000,
    transactions: 210,
    efficiency: 97.3,
    consumerRating: 4.9,
  },
  {
    id: 3,
    collector: "Michael Brown",
    zone: "East",
    totalCollected: 162800,
    target: 170000,
    transactions: 195,
    efficiency: 95.8,
    consumerRating: 4.7,
  },
  {
    id: 4,
    collector: "Sarah Davis",
    zone: "West",
    totalCollected: 195400,
    target: 190000,
    transactions: 255,
    efficiency: 102.8,
    consumerRating: 4.6,
  },
  {
    id: 5,
    collector: "David Miller",
    zone: "Central",
    totalCollected: 168500,
    target: 175000,
    transactions: 205,
    efficiency: 96.3,
    consumerRating: 4.5,
  },
  {
    id: 6,
    collector: "Jessica Lee",
    zone: "North",
    totalCollected: 155800,
    target: 165000,
    transactions: 180,
    efficiency: 94.4,
    consumerRating: 4.7,
  },
  {
    id: 7,
    collector: "James Wilson",
    zone: "South",
    totalCollected: 178500,
    target: 170000,
    transactions: 215,
    efficiency: 105.0,
    consumerRating: 4.8,
  },
  {
    id: 8,
    collector: "Emily Johnson",
    zone: "East",
    totalCollected: 145600,
    target: 160000,
    transactions: 175,
    efficiency: 91.0,
    consumerRating: 4.4,
  },
];

const CollectorPerformancePage = () => {
  const [dateRange, setDateRange] = useState({
    from: "2025-05-01",
    to: "2025-05-21",
  });
  const [zone, setZone] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("efficiency");
  const [collectorData, setCollectorData] = useState(INITIAL_COLLECTOR_DATA);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Calculate totals
  const totalCollected = collectorData.reduce(
    (sum, item) => sum + item.totalCollected,
    0
  );
  const totalTarget = collectorData.reduce((sum, item) => sum + item.target, 0);
  const avgEfficiency =
    Math.round(
      (collectorData.reduce((sum, item) => sum + item.efficiency, 0) /
        collectorData.length) *
        10
    ) / 10;
  const overallProgress = Math.round((totalCollected / totalTarget) * 100);

  // Filter by search query
  const filteredData = collectorData.filter((item) => {
    const searchString = searchQuery.toLowerCase();
    return (
      item.collector.toLowerCase().includes(searchString) ||
      item.zone.toLowerCase().includes(searchString)
    );
  });

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFilter = () => {
    // In a real app, this would filter based on the selected criteria
    // For this demo, we'll just sort the data based on the selected criteria
    let sortedData = [...INITIAL_COLLECTOR_DATA];

    if (sortBy === "efficiency") {
      sortedData.sort((a, b) => b.efficiency - a.efficiency);
    } else if (sortBy === "rating") {
      sortedData.sort((a, b) => b.consumerRating - a.consumerRating);
    } else if (sortBy === "amount") {
      sortedData.sort((a, b) => b.totalCollected - a.totalCollected);
    }

    setCollectorData(sortedData);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setDateRange({ from: "2025-05-01", to: "2025-05-21" });
    setZone("all");
    setSortBy("efficiency");
    setSearchQuery("");
    setCollectorData(INITIAL_COLLECTOR_DATA);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Collector Performance Report
        </h1>
        <p className="text-muted-foreground">
          Evaluate and compare collector efficiency and effectiveness
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
          <CardDescription>
            Set parameters to analyze collector performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="from-date">From Date</Label>
              <Input
                id="from-date"
                type="date"
                value={dateRange.from}
                onChange={(e) =>
                  setDateRange({ ...dateRange, from: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="to-date">To Date</Label>
              <Input
                id="to-date"
                type="date"
                value={dateRange.to}
                onChange={(e) =>
                  setDateRange({ ...dateRange, to: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="zone">Zone</Label>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger>
                  <SelectValue placeholder="All zones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All zones</SelectItem>
                  <SelectItem value="North">North</SelectItem>
                  <SelectItem value="South">South</SelectItem>
                  <SelectItem value="East">East</SelectItem>
                  <SelectItem value="West">West</SelectItem>
                  <SelectItem value="Central">Central</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortBy">Sort By</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efficiency">Efficiency</SelectItem>
                  <SelectItem value="rating">Consumer Rating</SelectItem>
                  <SelectItem value="amount">Collection Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  type="text"
                  placeholder="Search collector..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleFilter}>Generate Report</Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <CardTitle>Collector Performance</CardTitle>
              <CardDescription>
                {`Period: ${dateRange.from} to ${dateRange.to}`}
                {zone !== "all" && ` • Zone: ${zone}`}
              </CardDescription>
            </div>
            <div className="flex flex-col items-end mt-4 md:mt-0 space-y-1">
              <div className="text-2xl font-bold">
                ${totalCollected.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground flex gap-2">
                <span>{overallProgress}% of target</span>
                <span>•</span>
                <span>Avg. efficiency: {avgEfficiency}%</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Collector</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead className="text-right">Collected</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead className="text-center">Transactions</TableHead>
                <TableHead className="text-right">Efficiency %</TableHead>
                <TableHead className="text-center">Rating</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.collector}</TableCell>
                    <TableCell>{item.zone}</TableCell>
                    <TableCell className="text-right">
                      ${item.totalCollected.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      ${item.target.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.transactions}
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          item.efficiency >= 100
                            ? "text-green-600 font-medium"
                            : item.efficiency >= 95
                            ? "text-blue-600"
                            : item.efficiency >= 90
                            ? "text-amber-600"
                            : "text-red-600"
                        }
                      >
                        {item.efficiency}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center">
                        <span
                          className={
                            item.consumerRating >= 4.8
                              ? "text-green-600 font-medium"
                              : item.consumerRating >= 4.5
                              ? "text-blue-600"
                              : "text-amber-600"
                          }
                        >
                          {item.consumerRating}
                        </span>
                        <span className="text-yellow-500 ml-1">★</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CollectorPerformancePage;
